import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import makeAsyncScriptLoader from 'react-async-script';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';

// Created from https://cloud.google.com/recaptcha-enterprise/docs/api-ref-checkbox-keys#attributes_parameters
type RenderOptions = {
  sitekey: string;
  size?: 'compact' | 'normal' | 'invisible'; // invisible not in the def, but we've added it?
  action?: string;
  callback?: string | ((token: string | null) => void);
  'expired-callback'?: string | (() => void);
  'error-callback'?: string | (() => void);
};

export type GReCAPTCHA = {
  enterprise: {
    execute(id: number): void;
    reset(id: number): void;
    render<T>(node: T, options: RenderOptions): number;
  };
};

type Props = {
  sitekey: RenderOptions['sitekey'];
  action?: string;
  grecaptcha?: GReCAPTCHA;
  size?: RenderOptions['size'];
  onChange?: (token: string | null) => void;
  onExpired?: () => void;
  onErrored?: () => void;
};

type ExecutionRef = {
  onResolve?: (token: string) => void;
  onReject?: (error: Error) => void;
  hasRequested: boolean;
  resetRequired: boolean;
};

export type ReCAPTCHAEnterpriseHandle = {
  executeAsync: () => Promise<string>;
};

export const ReCAPTCHAEnterprise = forwardRef<ReCAPTCHAEnterpriseHandle, Props>(
  function ReCAPTCHAEnterprise(props, ref) {
    const { grecaptcha, onChange, onErrored, onExpired, size, sitekey, action } = props;
    const enterprise = grecaptcha?.enterprise;

    const [widgetId, setWidgetId] = useState<number | undefined>();

    const containerRef = useRef<HTMLDivElement>(null);

    const hasRendered = useRef(false);

    /**
     * The execution callbacks and states (hasRequest, resetRequired) etc.
     * are being tracked in a useRef since we don't want to react to its changes
     * but need to track it in-between renders.
     */
    const executionRef = useRef<ExecutionRef>({
      hasRequested: false,
      resetRequired: false,
    });

    const resetExecutionHandlers = useCallback(() => {
      delete executionRef.current.onResolve;
      delete executionRef.current.onReject;
    }, []);

    const reset = useCallback(() => {
      if (enterprise && typeof widgetId === 'number') {
        enterprise.reset(widgetId);
      }
    }, [enterprise, widgetId]);

    const handleError = useCallback(
      (cause: string) => {
        if (onErrored) {
          onErrored();
        }
        if (executionRef.current?.onReject) {
          executionRef.current.onReject(
            new Error(`ReCAPTCHAEnterprise rejected due to: ${cause}`, { cause })
          );
          resetExecutionHandlers();
        }
      },
      [onErrored, resetExecutionHandlers]
    );

    const handleChange = useCallback(
      (token: string | null) => {
        if (onChange) {
          onChange(token);
        }

        if (!token) {
          handleError('no-token');
        } else if (executionRef.current?.onResolve) {
          executionRef.current.onResolve(token);
          resetExecutionHandlers();
        }
      },
      [handleError, onChange, resetExecutionHandlers]
    );

    const handleExpired = useCallback(() => {
      if (onExpired) {
        onExpired();
        if (executionRef.current?.onReject) {
          executionRef.current.onReject(
            new Error('ReCAPTCHAEnterprise rejected due to expired token')
          );
          resetExecutionHandlers();
        }
      } else {
        handleChange(null);
      }
    }, [handleChange, onExpired, resetExecutionHandlers]);

    const execute = useCallback(
      (targetWidgetId?: number) => {
        const id = typeof targetWidgetId === 'undefined' ? widgetId : targetWidgetId;

        if (enterprise && id !== undefined) {
          // Tokens are cached, reset is needed after first execute
          if (executionRef.current?.resetRequired) {
            reset();
          }
          executionRef.current.resetRequired = true;
          enterprise.execute(id);
        } else {
          executionRef.current.hasRequested = true;
        }
      },
      [enterprise, reset, widgetId]
    );

    const renderExplicit = useCallback(() => {
      if (!sitekey) {
        captureErrorMessage('ReCAPTCHAEnterprise: recaptcha enterprise site key not provided');
        return;
      }

      // Check if grecaptcha is available and if it hasn't been rendered already
      if (!enterprise?.render || widgetId !== undefined || hasRendered.current) {
        return;
      }

      const wrapper = document.createElement('div');

      const id = enterprise.render<HTMLDivElement>(wrapper, {
        sitekey,
        action,
        size,
        callback: handleChange,
        'expired-callback': handleExpired,
        'error-callback': () =>
          // NOTE: recaptcha script does not provide a value to this callback: https://cloud.google.com/recaptcha/docs/api-ref-checkbox-keys#attributes_parameters
          // This is just our own error handler
          handleError('script invoked error-callback'),
      });

      setWidgetId(id);

      containerRef.current!.appendChild(wrapper);

      if (executionRef.current?.hasRequested) {
        executionRef.current.hasRequested = false;
        execute(id);
      }

      hasRendered.current = true;
    }, [
      action,
      enterprise,
      execute,
      handleChange,
      handleError,
      handleExpired,
      sitekey,
      size,
      widgetId,
    ]);

    useEffect(() => {
      /**
       * This has to run on every render, hence why there is no dependency array.
       * renderExplicit was called on componentDidMount & componentDidUpdate when it was a class component.
       */
      renderExplicit();
    });

    useEffect(() => {
      const delayOfCaptchaIframeRemoving = () => {
        const temporaryNode = document.createElement('div');
        document.body.appendChild(temporaryNode);
        temporaryNode.style.display = 'none';

        // move of the recaptcha to a temporary node

        while (containerRef.current?.firstChild) {
          temporaryNode.appendChild(containerRef.current.firstChild);
        }

        // delete the temporary node after reset will be done
        setTimeout(() => document.body.removeChild(temporaryNode), 5000);
      };

      return () => {
        if (widgetId !== undefined) {
          delayOfCaptchaIframeRemoving();
          reset();
          hasRendered.current = false;
        }
      };
    }, [reset, widgetId]);

    useImperativeHandle(
      ref,
      () => ({
        executeAsync() {
          return new Promise<string>((resolve, reject) => {
            executionRef.current.onResolve = resolve;
            executionRef.current.onReject = reject;
            execute();
          });
        },
      }),
      [execute]
    );

    return <div data-testid="ReCAPTCHAEnterprise" ref={containerRef} />;
  }
);

export default makeAsyncScriptLoader<Props, ReCAPTCHAEnterpriseHandle>(
  () => 'https://www.recaptcha.net/recaptcha/enterprise.js?onload=onloadcallback&render=explicit',
  {
    callbackName: 'onloadcallback',
    globalName: 'grecaptcha',
  }
)(ReCAPTCHAEnterprise);
